import { getProyect } from '@proxies/getProyect'
import { Disenyos } from '@types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

function normalizeText(text: string) {
  return text.replace(/(^|\/)0+/g, '$1')
}

export function useFetchProyects(
  orden: number,
  texto: string,
  verMisDisenyos: boolean | null,
  verDisenyosCreadoPorMi: boolean | null,
  verUFDisenyosVinculadoDis: boolean | null,
  tipoUsuario?: string,
  idTienda?: number,
  idDisenyador?: number,
  idUsuarioFinal?: number,
) {
  const [proyectsData, setProyectsData] = useState<Disenyos[]>([])
  const navigate = useNavigate()
  const lastCallIdRef = useRef<string | null>(null)

  const filterProjects = useCallback(
    (projects: Disenyos[]) => {
      if (!texto || texto.trim() === '') {
        return projects
      }

      const normalizedSearchText = normalizeText(texto.toLowerCase())

      return projects.filter(({ disenyo }) => {
        try {
          const fechaCreacion = normalizeText(
            new Date(disenyo.fechaCreacion).toLocaleDateString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            }),
          )

          const fechaUltimaModificacion = normalizeText(
            new Date(disenyo.fechaUltimaModificacion).toLocaleDateString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            }),
          )

          return (
            (disenyo.nombreProyecto?.toLowerCase() || '').includes(normalizedSearchText) ||
            (disenyo.nombre?.toLowerCase() || '').includes(normalizedSearchText) ||
            (disenyo.apellidos?.toLowerCase() || '').includes(normalizedSearchText) ||
            (disenyo.descripcion?.toLowerCase() || '').includes(normalizedSearchText) ||
            (disenyo.direccion?.toLowerCase() || '').includes(normalizedSearchText) ||
            (disenyo.codPostal?.toLowerCase() || '').includes(normalizedSearchText) ||
            (disenyo.idVersiones?.toString() || '').includes(normalizedSearchText) ||
            fechaCreacion.includes(normalizedSearchText) ||
            fechaUltimaModificacion.includes(normalizedSearchText) ||
            (disenyo.urlVisor && disenyo.urlVisor.toLowerCase().includes(normalizedSearchText)) ||
            (disenyo.vFecha && disenyo.vFecha.toLowerCase().includes(normalizedSearchText))
          )
        } catch (error) {
          console.error('Error filtrando proyecto:', error, disenyo)
          return false
        }
      })
    },
    [texto],
  )

  const fetchAndSetProjects = useCallback(async () => {
    const callId = Math.random().toString(36).substring(7)
    lastCallIdRef.current = callId

    try {
      const projects = await getProyect(
        navigate,
        orden,
        texto,
        verMisDisenyos,
        verDisenyosCreadoPorMi,
        verUFDisenyosVinculadoDis,
        tipoUsuario,
        idTienda,
        idDisenyador,
        idUsuarioFinal,
      )

      if (lastCallIdRef.current !== callId) {
        return
      }

      const enrichedProjects = projects.map((project) => ({
        ...project,
        disenyo: {
          ...project.disenyo,
          urlVisor: project.disenyo.urlVisor || '',
          vFecha: project.disenyo.vFecha || '',
        },
      }))

      const filteredProjects = filterProjects(enrichedProjects)

      setProyectsData(filteredProjects)
    } catch (error) {
      if (lastCallIdRef.current === callId) {
        console.error(`❌ [${callId}] Failed to fetch projects`, error)
      }
    }
  }, [
    orden,
    texto,
    verMisDisenyos,
    verDisenyosCreadoPorMi,
    verUFDisenyosVinculadoDis,
    filterProjects,
    navigate,
    tipoUsuario,
    idTienda,
    idDisenyador,
    idUsuarioFinal,
  ])

  useEffect(() => {
    fetchAndSetProjects()
  }, [fetchAndSetProjects])

  return { proyectsData, refetch: fetchAndSetProjects }
}
